import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Database, Copy, ExternalLink, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const LookerDocsPanel = () => {
  const [datasources, setDatasources] = useState([]);
  const [loading, setLoading] = useState(false);
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Public listing — no key needed to read the published documentation; the
  // key is enforced server-side when actually pulling data.
  const fetchDS = async () => {
    setLoading(true);
    try {
      // Try public route documentation — we still hit the endpoint with a placeholder header
      // to keep the example real. If unauthorised we still document the schema.
      const r = await fetch(`${apiBase}/reports/looker/datasources`, { headers: { 'X-API-Key': '__placeholder__' } });
      if (r.ok) {
        const d = await r.json();
        setDatasources(d.datasources || []);
      }
    } catch (_e) {
      /* documentation will fall back to DEFAULT_DS list */
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDS(); }, []);

  const DEFAULT_DS = [
    { id: 'leads', name: 'Leads', endpoint: '/api/reports/looker/data/leads' },
    { id: 'marketing_spend', name: 'Marketing Spend', endpoint: '/api/reports/looker/data/marketing_spend' },
    { id: 'payroll', name: 'Payroll', endpoint: '/api/reports/looker/data/payroll' },
    { id: 'targets', name: 'Targets', endpoint: '/api/reports/looker/data/targets' },
    { id: 'stage_funnel', name: 'Stage Funnel', endpoint: '/api/reports/looker/data/stage_funnel' },
    { id: 'source_performance', name: 'Source Performance', endpoint: '/api/reports/looker/data/source_performance' },
    { id: 'geo_hotspot', name: 'Geographical Hotspot', endpoint: '/api/reports/looker/data/geo_hotspot' },
    { id: 'lead_aging', name: 'Lead Aging', endpoint: '/api/reports/looker/data/lead_aging' },
    { id: 'dropout_ratio', name: 'Dropout Ratio', endpoint: '/api/reports/looker/data/dropout_ratio' },
  ];
  const list = datasources.length ? datasources : DEFAULT_DS;

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-4" data-testid="looker-docs-panel">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Database className="h-4 w-4 text-primary" /> Looker Studio Integration</CardTitle>
          <CardDescription>Connect Looker Studio to all CRM reports via secure, read-only JSON endpoints. New reports added on the server automatically expose themselves via the <code className="px-1 bg-muted rounded">/reports/looker/datasources</code> discovery endpoint — no Looker config changes required.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <p>Authenticate every request via header: <code className="px-1.5 py-0.5 bg-muted rounded">X-API-Key: &lt;LOOKER_API_KEY&gt;</code></p>
          </div>
          <p className="text-muted-foreground text-xs">The API key lives in the backend <code>.env</code> as <code>LOOKER_API_KEY</code>. Rotate it any time by updating the env and restarting the backend.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div className="border rounded p-3 bg-muted/30">
              <p className="text-xs font-semibold mb-1">1. Discovery endpoint</p>
              <p className="text-xs text-muted-foreground mb-2">Returns the full list of datasets currently exposed.</p>
              <code className="block text-[11px] break-all bg-background border rounded p-2">GET {apiBase}/reports/looker/datasources</code>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => copy(`${apiBase}/reports/looker/datasources`)} data-testid="copy-discovery">
                <Copy className="h-3 w-3 mr-1" /> Copy URL
              </Button>
            </div>
            <div className="border rounded p-3 bg-muted/30">
              <p className="text-xs font-semibold mb-1">2. Data endpoint (generic)</p>
              <p className="text-xs text-muted-foreground mb-2">Returns <code>{`{schema, rows}`}</code> — Looker-friendly shape.</p>
              <code className="block text-[11px] break-all bg-background border rounded p-2">GET {apiBase}/reports/looker/data/{`{dataset}`}?startDate=YYYY-MM-DD&amp;endDate=YYYY-MM-DD</code>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => copy(`${apiBase}/reports/looker/data/leads`)} data-testid="copy-data-endpoint">
                <Copy className="h-3 w-3 mr-1" /> Copy sample
              </Button>
            </div>
          </div>

          <div className="border rounded p-3 bg-muted/30 mt-2">
            <p className="text-xs font-semibold mb-1">3. cURL example</p>
            <code className="block text-[11px] break-all bg-background border rounded p-2 whitespace-pre">{`curl -H "X-API-Key: $LOOKER_API_KEY" \\\n     "${apiBase}/reports/looker/data/leads?startDate=2026-01-01&endDate=2026-12-31"`}</code>
          </div>

          <div className="text-xs text-muted-foreground">
            <p><strong>Looker Studio setup:</strong></p>
            <ol className="list-decimal ml-5 space-y-1 mt-1">
              <li>In Looker Studio → <em>Add data</em> → choose <em>JSON / Cloud Function / Community Connector</em>.</li>
              <li>Paste any data endpoint URL above and add the <code>X-API-Key</code> header.</li>
              <li>Looker reads the <code>schema</code> array to auto-detect dimensions (STRING/DATE/NUMBER) and the <code>rows</code> array as records.</li>
              <li>Build any chart freely. Server-side filters via <code>startDate</code> &amp; <code>endDate</code> query params.</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Available datasets ({list.length})</CardTitle>
          <CardDescription>All endpoints below return <code>{`{ schema: [...], rows: [...], rowCount }`}</code></CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="looker-datasource-list">
            {list.map((ds) => (
              <div key={ds.id} className="border rounded p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{ds.name}</p>
                  <code className="text-[11px] text-muted-foreground">{ds.endpoint}</code>
                </div>
                <Button size="sm" variant="ghost" onClick={() => copy(`${process.env.REACT_APP_BACKEND_URL}${ds.endpoint}`)} data-testid={`copy-ds-${ds.id}`}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LookerDocsPanel;
